import { Link, useParams } from 'react-router-dom';
import { Card, Badge, LoadingState, StatePanel } from '@sakhtar/design-system/react';
import { MobilePageHead } from '../../shell/MobilePageHead.js';
import { useApiGet } from '../../lib/useApiGet.js';
import { tenantBasePath } from '../../lib/tenant.js';
import { displayJalaliDate } from '../../lib/date/displayJalali.js';
import type { TaskListResponse } from '../../lib/types/tasks.js';
import { NO_PROJECT_LABEL } from '../../lib/types/projects.js';

/**
 * UI_REFERENCE_SOURCE:
 * section: Sakhtar Mobile App.dc.html §FRAME 07 MY WORK SHEET
 * canonical pattern: task row list
 * state reference: ready · empty · permission_denied · network_error
 *
 * Real data (GET t/:slug/api/v1/projects/tasks?assignee=me — "my work",
 * same list as the desktop topbar «کارهای من» popover). Each row opens the
 * task detail page (/projects/tasks/:ref), where the status is changed.
 */
export function TasksPage() {
  const { slug } = useParams<{ slug: string }>();
  const result = useApiGet<TaskListResponse>(
    slug ? `${tenantBasePath(slug)}/api/v1/projects/tasks?assignee=me&limit=50` : null,
    [slug],
  );

  if (!slug) return null;

  return (
    <div style={{ padding: 16 }}>
      <MobilePageHead title="کارها" />
      {result.status === 'loading' ? <LoadingState /> : null}
      {result.status === 'error' ? (
        <StatePanel
          variant={result.error.status === 403 ? 'permission_denied' : 'network_error'}
          title={result.error.status === 403 ? 'دسترسی ندارید' : 'کارها قابل نمایش نیست'}
        />
      ) : null}
      {result.status === 'ready' ? (
        result.data.tasks.length === 0 ? (
          <StatePanel variant="empty" title="کاری برای شما ثبت نشده" />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {result.data.tasks.map((task) => (
              <Link
                key={task.ref}
                to={`${tenantBasePath(slug)}/projects/tasks/${encodeURIComponent(task.ref)}`}
                aria-label={`مشاهدهٔ کار ${task.title}`}
                style={{ textDecoration: 'none', color: 'inherit' }}
              >
                <Card>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                      <strong>{task.title}</strong>
                      <span style={{ fontSize: 12, opacity: 0.7 }}>
                        {task.projectRef ?? NO_PROJECT_LABEL}
                        {task.dueDate ? ` · سررسید ${displayJalaliDate(task.dueDate)}` : ''}
                      </span>
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 8,
                      }}
                    >
                      <Badge tone="info">{task.statusLabel}</Badge>
                      <span
                        className="sk-btn sk-btn--secondary sk-btn--sm"
                        style={{ minHeight: 40 }}
                      >
                        مشاهده
                      </span>
                    </div>
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        )
      ) : null}
    </div>
  );
}
